import { useMemo } from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import { Checkbox } from '../../../components/ui/checkbox';
import { Textarea } from '../../../components/ui/textarea';
import { StepHeader } from '../../../components/common/StepHeader';
import { EstimateCard } from '../../../components/common/EstimateCard';
import { useWizardFlow } from '../../../hooks/useWizardFlow';
import { MOUNTING_QUESTIONS } from '../handymanData';
import { calculateHandymanEstimate } from '../handymanPricing';
import { shouldShowQuestion } from '../handymanValidation';

/**
 * Mounting/Assembly sub-flow component
 * Handles TV mounting, furniture assembly, wall mounting, etc.
 */
export const MountingFlow = ({
  answers,
  setAnswers,
  onComplete,
  onBack
}) => {
  // Use shared wizard flow hook (no auto-advance for Handyman)
  const {
    currentQuestion,
    isLastQuestion,
    canProceed,
    goNext,
    goBack,
    handleRadioAnswer,
    handleCheckboxChange,
    handleTextAnswer
  } = useWizardFlow({
    questions: MOUNTING_QUESTIONS,
    answers,
    setAnswers,
    onComplete,
    onBack,
    shouldShowQuestion,
    autoAdvanceOnRadio: false
  });
  
  // Calculate running total estimate across ALL selected handyman services
  const estimatedPrice = useMemo(
    () => calculateHandymanEstimate(answers),
    [answers]
  );
  
  if (!currentQuestion) {
    return <div>Loading...</div>;
  }
  
  return (
    <div className="space-y-6">
      <StepHeader
        title={currentQuestion.question}
        onBack={goBack}
        showBack={true}
      />
      
      <div className="space-y-4">
        {/* Radio Question */}
        {currentQuestion.type === 'radio' && (
          <RadioGroup
            value={answers[currentQuestion.id] || ""}
            onValueChange={handleRadioAnswer}
            className="space-y-3"
          >
            {currentQuestion.options.map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-4 p-4 bg-gray-50 border rounded-xl cursor-pointer transition-all hover:border-[#0FA3B1]/50 ${
                  answers[currentQuestion.id] === option.value
                    ? "border-[#0FA3B1] bg-[#0FA3B1]/5"
                    : "border-gray-200"
                }`}
                data-testid={`option-${option.value}`}
              >
                <RadioGroupItem
                  value={option.value}
                  className="border-2 border-gray-300 text-[#0FA3B1]"
                />
                <span className="text-[#1F2933] flex-1">{option.label}</span>
              </label>
            ))}
          </RadioGroup>
        )}
        
        {/* Checkbox Question */}
        {currentQuestion.type === 'checkbox' && (
          <div className="space-y-3">
            {currentQuestion.options.map((option) => {
              const isChecked = (answers[currentQuestion.id] || []).includes(option.value);
              return (
                <label
                  key={option.value}
                  className={`flex items-center gap-4 p-4 bg-gray-50 border rounded-xl cursor-pointer transition-all hover:border-[#0FA3B1]/50 ${
                    isChecked
                      ? "border-[#0FA3B1] bg-[#0FA3B1]/5"
                      : "border-gray-200"
                  }`}
                  data-testid={`option-${option.value}`}
                >
                  <Checkbox
                    checked={isChecked}
                    onCheckedChange={(checked) => handleCheckboxChange(option.value, checked)}
                    className="border-2 border-gray-300 data-[state=checked]:bg-[#0FA3B1] data-[state=checked]:border-[#0FA3B1]"
                  />
                  <span className="text-[#1F2933] flex-1">{option.label}</span>
                </label>
              );
            })}
          </div>
        )}
        
        {/* Text Question */}
        {currentQuestion.type === 'text' && (
          <Textarea
            value={answers[currentQuestion.id] || ''}
            onChange={(e) => handleTextAnswer(e.target.value)}
            placeholder={currentQuestion.placeholder || ''}
            className="bg-gray-50 border-gray-200 text-[#1F2933] resize-none min-h-[100px]"
            data-testid={`text-input-${currentQuestion.id}`}
          />
        )}
      </div>
      
      {/* Running Estimate */}
      <EstimateCard priceRange={estimatedPrice} />
      
      {/* Navigation */}
      <Button 
        onClick={goNext}
        disabled={!canProceed}
        className="w-full bg-[#0FA3B1] hover:bg-[#0D8C98] text-white py-6 rounded-xl disabled:opacity-50"
        data-testid={isLastQuestion ? "mounting-complete" : "mounting-next"}
      >
        {isLastQuestion ? "Continue" : "Next"} <ArrowRight className="w-5 h-5 ml-2" />
      </Button>
    </div>
  );
};

export default MountingFlow;
